import { Box } from '@everlywell/ui-kit';
import CardBuilder, {
  CardBuilderProps,
  ComponentNames as CardComponentNames,
} from 'components/CardBuilder';
import { parseInfoCardV2 } from 'components/CardBuilder/InfoCardV2/InfoCardV2.parser';
import { CardsAccordionProps } from 'components/CardsAccordion';
import { MultipleCardsSwiperProps } from 'components/MultipleCardsSwiper';
import { ProductCardV2Props } from 'components/ProductCardV2';
import { parseProductCardV2 } from 'components/ProductCardV2/ProductCardV2.parser';
import React from 'react';
import { Promo } from 'utils/helpers/activePromoHelpers';
import { Section, Subsections } from 'utils/types/contentfulTypes';

type ComponentParserTypes = 'InfoCardV2' | 'ProductCardV2';

export const parseBulletPoints = (bulletPointsSubsection: Section) =>
  bulletPointsSubsection?.subsections?.map((bulletPoint) => {
    const {
      context: { icon },
      description,
    } = bulletPoint?.metadata ?? {};

    return {
      iconName: icon,
      text: description?.description ?? '',
    };
  });

export const parseAccordionCards = (
  cardGroupsItems: Array<CardBuilderProps<CardComponentNames>[]>,
) =>
  cardGroupsItems?.map((cardGroup) => {
    const button = cardGroup?.find(
      (card) => card?.componentName === 'InfoCardV2',
    ) as CardBuilderProps<'InfoCardV2'>;

    const buttonIndex = cardGroup?.indexOf(button);
    const content = cardGroup?.filter((_, index) => index !== buttonIndex);

    return {
      button,
      content,
    };
  }) as CardsAccordionProps['items'];

export const parseCarouselCards = (
  cardGroupItems: Array<CardBuilderProps<CardComponentNames>[]>,
) =>
  cardGroupItems
    ?.flat()
    ?.filter(({ componentName }) => componentName)
    ?.map(({ componentName, ...cardProps }, index: number) => {
      if (componentName === 'ProductCardV2') {
        const variant = (cardProps as ProductCardV2Props)?.variant;

        return (
          <Box
            key={`carousel-card-v2-${index}`}
            width={
              variant === 'standard'
                ? ['100%', '280px', '280px']
                : ['100%', '320px', '320px']
            }
            height="100%"
          >
            <CardBuilder
              componentName="ProductCardV2"
              {...(cardProps as ProductCardV2Props)}
            />
          </Box>
        );
      }

      return (
        <CardBuilder
          key={`card-builder-${index}`}
          componentName={componentName}
          {...(cardProps as any)}
        />
      );
    }) as MultipleCardsSwiperProps['cards'];

const componentParsers = {
  InfoCardV2: parseInfoCardV2,
  ProductCardV2: parseProductCardV2,
};

export const createCardComponents = (
  cardGroups: Subsections[],
  advertisedPromos: Promo[] = [],
) =>
  cardGroups
    ?.map((accordion) => {
      const cards = accordion?.subsections;

      const items = cards
        ?.filter(({ componentName }) => componentName)
        ?.map((card) => {
          const componentName = (card?.componentName ??
            '') as ComponentParserTypes;

          const parseFunction = componentParsers[componentName];
          if (parseFunction) {
            return {
              componentName,
              ...parseFunction(card, advertisedPromos),
            };
          }
          return null;
        });

      return items;
    })
    .filter((item) => item !== null);
